/**
 * EmployerFlow — "where do this uni's graduates go?" widget.
 *
 * Uses `/api/universities/:id/employer-flow` to render a horizontal
 * proportional-bar visualization (a simplified Sankey — one source
 * node = the uni, N destination nodes = firms). We deliberately
 * avoid a real Sankey library (would add ~50KB) — a CSS-only bar
 * chart tells the same story faster.
 *
 * Only rendered for editorial-tier unis where destinations coverage
 * is deep enough to be trustworthy. Non-editorial unis get nothing
 * rather than a misleading "we have no data on Ohio State's grads"
 * chart.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Building2, ArrowRight, ExternalLink, Users } from "lucide-react";

const BAR_COLORS = [
  "#4C1D95", "#7C3AED", "#A855F7", "#C084FC", "#D8B4FE",
  "#E9D5FF", "#F3E8FF", "#EDE9FE", "#DDD6FE", "#C4B5FD",
];

// Strength-band presentation. We deliberately swap precise decimals
// for named bands because our underlying pct_of_cohort values are
// editorial estimates drawn from public career reports + firm target
// lists, not audited hire counts. A band communicates the signal
// without overclaiming precision. See the disclaimer strip below.
const BAND_LABEL = {
  strong:   { label: "Strong pipeline",   bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300" },
  moderate: { label: "Regular hires",      bg: "bg-amber-100",   text: "text-amber-800",   border: "border-amber-300" },
  emerging: { label: "Emerging pipeline", bg: "bg-stone-100",   text: "text-stone-700",   border: "border-stone-300" },
  trace:    { label: "Occasional",         bg: "bg-stone-50",    text: "text-stone-500",   border: "border-stone-200" },
};

// Firm-name → likely /how-to-get-into slug. Rough client-side
// derivation — the backend firm catalogue slug uses kebab-case of
// the firm name. Not perfect (McKinsey vs mckinsey-and-company) but
// good enough for the "click through to guide" UX; unknown slugs
// just 404 gracefully to /firm-guides.
function firmSlug(name) {
  return name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[.']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function EmployerFlow({ uniId, uniName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Selected subject_area filter — "" means aggregate across all subjects.
  const [subject, setSubject] = useState("");

  useEffect(() => {
    if (!uniId) return;
    let cancelled = false;
    setLoading(true);
    const q = new URLSearchParams({ limit: "10" });
    if (subject) q.set("subject", subject);
    api.get(`/universities/${uniId}/employer-flow?${q.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [uniId, subject]);

  if (loading && !data) return null;
  const employers = data?.employers || [];
  const availableSubjects = data?.available_subjects || [];
  // Don't render the widget at all if we have no data whatsoever —
  // but keep it up when a subject filter simply narrows to zero (the
  // user can toggle back to All).
  if (employers.length === 0 && !subject && availableSubjects.length === 0) return null;

  const maxPct = employers.length > 0
    ? Math.max(...employers.map((e) => e.avg_pct_of_cohort), 1)
    : 1;
  const total = employers.reduce((n, e) => n + (e.avg_pct_of_cohort || 0), 0) || 1;

  return (
    <section
      data-testid="employer-flow-widget"
      className="mt-8 rounded-lg border border-stone-200 bg-white p-6"
    >
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-[#4C1D95]" />
        <div className="label-uppercase text-[#4C1D95]">Where graduates go</div>
      </div>
      <h3 className="mt-2 font-editorial text-2xl text-[#002147]">
        Top employers of {uniName} {subject ? `${subject.toLowerCase()} ` : ""}graduates
      </h3>
      <p className="mt-2 text-sm text-stone-600 max-w-2xl">
        Which employers show up repeatedly in {uniName}'s{" "}
        {subject ? subject.toLowerCase() + " " : ""}graduate destinations
        across {data.total_degrees_with_data}{" "}
        {subject ? subject.toLowerCase() : ""} degree{data.total_degrees_with_data === 1 ? "" : "s"}
        {" "}with published data. Bar width = relative frequency; band label = strength of signal.
      </p>

      {/* Subject chip row — flips the whole widget to a subject slice */}
      {availableSubjects.length > 1 && (
        <div
          data-testid="employer-flow-subject-chips"
          className="mt-5 flex flex-wrap gap-1.5"
        >
          <button
            type="button"
            onClick={() => setSubject("")}
            data-testid="employer-flow-subject-chip-all"
            className={`text-[11px] font-underground-sc tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
              subject === ""
                ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95]"
            }`}
          >
            All ({availableSubjects.reduce((n, s) => n + s.degrees_count, 0)})
          </button>
          {availableSubjects.map((s) => {
            const active = subject === s.subject;
            return (
              <button
                type="button"
                key={s.subject}
                data-testid={`employer-flow-subject-chip-${s.subject}`}
                onClick={() => setSubject(active ? "" : s.subject)}
                className={`text-[11px] font-underground-sc tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                  active
                    ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                    : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95]"
                }`}
              >
                {s.subject} ({s.degrees_count})
              </button>
            );
          })}
        </div>
      )}

      {/* Data-confidence disclaimer — shown for every widget now that
          we've moved to strength bands. Explains what a "band" means
          and links to sources so families can go check. */}
      <div
        data-testid="employer-flow-pattern-disclaimer"
        className="mt-4 rounded-md border border-amber-300 bg-amber-50/60 px-3 py-2 text-[12px] text-stone-700"
      >
        Bands (<span className="font-underground-sc tracking-wider text-emerald-800">strong</span> ·{" "}
        <span className="font-underground-sc tracking-wider text-amber-800">regular</span> ·{" "}
        <span className="font-underground-sc tracking-wider text-stone-600">emerging</span>) reflect
        the signal in public sources — uni careers reports + High Fliers Top 100 + firm
        target-school lists. We don't publish precise cohort percentages because published data
        rarely supports that precision. Rows drawn from editorial estimates are flagged{" "}
        <span className="font-underground-sc tracking-wider text-amber-800">PATTERN</span>.
        <Link to="/methodology" className="ml-1 underline text-[#4C1D95]">Full methodology</Link>.
      </div>

      {/* Feb 2026 · Task 4 — LinkedIn trust caption. Shown when the
          uni-wide LinkedIn "alumni work at" scrape has landed. This
          gives families a "verified via LinkedIn" signal alongside
          the editorial estimates. */}
      {data?.linkedin_meta?.tier && (
        <div
          data-testid="employer-flow-linkedin-caption"
          className="mt-2 rounded-md border border-sky-300 bg-sky-50/70 px-3 py-2 text-[12px] text-stone-700"
        >
          <Users className="inline h-3 w-3 mr-1 text-sky-800" aria-hidden />
          <span className="font-medium text-sky-900">
            LinkedIn-verified employers overlay:
          </span>{" "}
          {data.linkedin_meta.employers_count} firms cross-checked against{" "}
          public LinkedIn "Where alumni work" data
          {data.linkedin_meta.ingested_at && (
            <> · <span className="font-mono text-[11px] text-stone-500">
              as of {new Date(data.linkedin_meta.ingested_at).toLocaleDateString(undefined, {year:"numeric", month:"short"})}
            </span></>
          )}. Rows tagged{" "}
          <span className="font-underground-sc tracking-wider text-sky-800">LinkedIn</span>{" "}
          are surfaced from that overlay when they're not in the editorial list.
        </div>
      )}

      {/* Per-uni source citations */}
      {(data?.sources || []).length > 0 && (
        <details
          data-testid="employer-flow-sources"
          className="mt-3 rounded-md border border-stone-200 bg-stone-50/60"
        >
          <summary className="cursor-pointer px-3 py-2 text-[11px] font-underground-sc tracking-wider text-stone-600 hover:text-[#4C1D95]">
            {data.sources.length} public source{data.sources.length === 1 ? "" : "s"} used for this uni
          </summary>
          <ul className="px-3 pb-3 pt-1 space-y-1.5">
            {data.sources.map((s) => (
              <li key={s.url} className="text-[12px]">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`employer-flow-source-link-${s.year}`}
                  className="inline-flex items-center gap-1 text-[#4C1D95] hover:underline"
                >
                  {s.name} {s.year && <span className="text-stone-500">· {s.year}</span>}
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}

      {employers.length === 0 ? (
        <div
          data-testid="employer-flow-empty-filter"
          className="mt-6 rounded-md border border-amber-300 bg-amber-50/60 p-4 text-[13px] text-stone-700"
        >
          No destinations data yet for {subject} at {uniName}. Try another
          subject or view all.
        </div>
      ) : (subject && employers.every((e) => e.is_editorial_pattern || e.is_subject_cluster_pattern)) ? (
        <div
          data-testid="employer-flow-uniwide-pattern-notice"
          className="mt-6 rounded-md border border-amber-300 bg-amber-50/60 p-4 text-[13px] text-stone-700 leading-relaxed"
        >
          <div className="font-underground-sc text-[10px] tracking-wider text-amber-800 mb-1">
            No verified per-subject data
          </div>
          <p>
            {uniName}'s published destinations don't include a firm-verified
            employer breakdown for <span className="font-medium">{subject}</span> yet — the rows
            we could show here would be drawn entirely from an editorial{" "}
            {employers.some((e) => e.is_subject_cluster_pattern) ? "subject-cluster pattern" : "uni-wide pattern"}{" "}
            (the same {employers.length} firms other {subject.toLowerCase()} cohorts get by default),
            so we're hiding them rather than dress up a canned list as real data.
            Switch back to <button
              type="button"
              onClick={() => setSubject("")}
              className="underline text-[#4C1D95] hover:text-[#4C1D95]/80"
              data-testid="employer-flow-uniwide-pattern-back-to-all"
            >All</button> to see the aggregate signal with its pattern caveat, or read our{" "}
            <Link to="/methodology" className="underline text-[#4C1D95]">methodology</Link>{" "}
            on how we're improving per-subject depth.
          </p>
        </div>
      ) : (
        <>
          {/* Stacked "flow" strip — visual signal of firm concentration */}
          <div
            data-testid="employer-flow-stacked-strip"
            className="mt-6 flex h-3 w-full rounded-full overflow-hidden border border-stone-200"
            aria-hidden
          >
            {employers.map((e, i) => (
              <div
                key={e.name}
                style={{
                  width: `${(e.avg_pct_of_cohort / total) * 100}%`,
                  backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
                }}
                title={`${e.name} · ${e.avg_pct_of_cohort}%`}
              />
            ))}
          </div>

          {/* Individual employer rows */}
          <div className="mt-5 space-y-2">
            {employers.map((e, i) => {
              const slug = firmSlug(e.name);
              const widthPct = (e.avg_pct_of_cohort / maxPct) * 100;
              return (
                <div
                  key={e.name}
                  data-testid={`employer-flow-row-${i}`}
                  className="grid grid-cols-[minmax(0,180px)_1fr_auto] items-center gap-3 text-sm"
                >
                  <div className="min-w-0">
                    <Link
                      to={`/how-to-get-into/${slug}`}
                      className="text-[#4C1D95] hover:underline truncate font-medium block"
                    >
                      {e.name}
                    </Link>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1">
                      {e.is_editorial_pattern && (
                        <span
                          data-testid={`employer-flow-row-${i}-pattern-chip`}
                          title="Pattern-based — sourced from published careers reports + firm target-school lists, not audited HESA data."
                          className="inline-block text-[9px] font-underground-sc tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-1.5 py-[1px]"
                        >
                          Pattern
                        </span>
                      )}
                      {e.is_subject_cluster_pattern && !e.is_editorial_pattern && (
                        <span
                          data-testid={`employer-flow-row-${i}-cluster-chip`}
                          title="Subject-cluster pattern — this firm appears repeatedly on published outcome patterns for this subject cluster, but the per-uni share isn't yet audited."
                          className="inline-block text-[9px] font-underground-sc tracking-wider text-sky-800 bg-sky-100 border border-sky-300 rounded-full px-1.5 py-[1px]"
                        >
                          Subject pattern
                        </span>
                      )}
                      {e.is_linkedin_only && (
                        <span
                          data-testid={`employer-flow-row-${i}-linkedin-only-chip`}
                          title="Surfaced from LinkedIn's public 'Where alumni work' snippets — this firm isn't in our editorial destinations list yet, but appears in LinkedIn's aggregate for this uni."
                          className="inline-block text-[9px] font-underground-sc tracking-wider text-sky-900 bg-sky-100 border border-sky-400 rounded-full px-1.5 py-[1px]"
                        >
                          LinkedIn
                        </span>
                      )}
                      {e.linkedin_count != null && (
                        <span
                          data-testid={`employer-flow-row-${i}-linkedin-count`}
                          title="Approx. LinkedIn alumni-count captured from the uni's public 'Where alumni work' snippet."
                          className="inline-flex items-center gap-0.5 text-[9px] font-mono tabular-nums tracking-wider text-emerald-900 bg-emerald-100 border border-emerald-400 rounded-full px-1.5 py-[1px]"
                        >
                          <Users className="h-2 w-2" aria-hidden />
                          {e.linkedin_count.toLocaleString()} alumni
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="relative h-6 bg-stone-100 rounded overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 rounded"
                      style={{
                        width: `${widthPct}%`,
                        backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
                      }}
                    />
                    <div className="absolute inset-0 flex items-center px-2 text-[11px] font-mono text-stone-700 tracking-wider">
                      {e.sample_subjects.slice(0, 2).join(" · ")}
                    </div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    {(() => {
                      const band = BAND_LABEL[e.strength_band] || BAND_LABEL.emerging;
                      return (
                        <span
                          data-testid={`employer-flow-row-${i}-band`}
                          title="Signal band based on public sources — precise cohort percentages are not published."
                          className={`inline-block text-[9px] font-underground-sc tracking-wider ${band.bg} ${band.text} border ${band.border} rounded-full px-2 py-[2px]`}
                        >
                          {band.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="mt-5 pt-4 border-t border-stone-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-underground-sc tracking-wider text-stone-500">
        <span>Editorial · Averaged from graduate destinations surveys</span>
        <Link to="/methodology" className="text-[#4C1D95] underline">Methodology</Link>
        <span>·</span>
        <Link to="/firm-guides" className="inline-flex items-center gap-0.5 text-[#4C1D95] hover:underline">
          Browse all firm guides <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </section>
  );
}
